import React from "react";
import { observer } from "mobx-react";
import type { IResourcesStore } from "../../../../resources/ResourcesStore";
import { injectFromApp } from "../../../inject";
import type { IProductInfoStore } from "../../../product-info/ProductInfoStore";
import { ThirdPartySoftwareComponent } from "./ThirdPartySoftwareComponent";
import sanitizeHtml from "sanitize-html";
import type { IServerInfoStore } from "../../../server-info/ServerInfoStore";
import { DobSelect } from "@dedalusdiit/dob-components";
import { IAboutUsDialogStore } from "./AboutUsDialogStore";
import { CountryModelExtended } from "../../../country/CountryModelExtended";

interface IAboutUsContentComponentProps {
    productInfoStore?: IProductInfoStore;
    serverInfoStore?: IServerInfoStore;
    resourcesStore?: IResourcesStore;
    aboutUsDialogStore?: IAboutUsDialogStore;
}

@injectFromApp<IAboutUsContentComponentProps>(stores => ({
    productInfoStore: stores.productInfoStore,
    serverInfoStore: stores.serverInfoStore,
    resourcesStore: stores.resourcesStore,
    aboutUsDialogStore: stores.aboutUsDialogStore,
}))

@observer
export class AboutUsContentComponent extends React.Component<IAboutUsContentComponentProps> {
    formatSelectOptionCountry = (country: CountryModelExtended) => {
        return (
            <div className={ "country-option" }>
                { country.flag && <img src={ country.flag } alt={ country.translatedName }/> } { country.translatedName }
            </div>
        );
    };

    handleCountryChange = (country: CountryModelExtended | null) => {
        const { aboutUsDialogStore } = this.props;
        aboutUsDialogStore!.setSelectedCountry(country);
    };

    render() {
        const { resourcesStore, productInfoStore, serverInfoStore, aboutUsDialogStore } = this.props;
        const { resources } = resourcesStore!;
        const { serverCode, machineName } = serverInfoStore!;
        const { version, productReleaseDate, manualsRevisionDate, softwareIdentificationNumber, isMedicalDevice } = productInfoStore!;
        const { customizedCountries, selectedCountry, showCountrySelector } = aboutUsDialogStore!;

        const versionWithoutBuildNumber = version.split(".", 3).join(".");
        
        return (
            <div className={ "about-us-content-component" }>
                { showCountrySelector && (
                    <div className={ "country-selector" }>
                        <DobSelect
                            direction={ "horizontal" }
                            options={ customizedCountries }
                            optionSelected={ selectedCountry }
                            clearable={ true }
                            optionLabel={ "translatedName" }
                            optionValue={ "id" }
                            placeholder={ resources.SelectASpecificCountry }
                            formatOptionLabel={ this.formatSelectOptionCountry }
                            onChange={ this.handleCountryChange }
                        />
                    </div>
                ) }
                <div className={ "primary-content" }>
                    <div className={ "column-content" }>
                        <div className="support">
                            <h2>{ resources.Support }</h2>
                            <p dangerouslySetInnerHTML={ { __html: sanitizeHtml(resources.SupportInfoAndLink.replace("{0}", "/POW3/Support")) } }/>
                            <p>
                                { resources.SupportTechnical }
                                <br/>
                                <a href="/POW3/Resource/GetTeamviewer">{ resources.ClickHereToStartTeamViewer }</a>
                            </p>
                        </div>
                        <div className="company-information">
                            <h2>{ resources.CompanyInformation }</h2>
                            <div className={ "row-content" }>
                                <img src="/POW4/img/icons/icnCompanyInformation.png" alt={ resources.CompanyInformation }/>
                                <p>
                                    DH Healthcare GmbH
                                    <br/>
                                    Konrad-Zuse-Platz 1-3
                                    <br/>
                                    53227 Bonn, Germany
                                    <br/>
                                    { resources.Website }:{ " " }
                                    <a href={ "https://www.dedalusgroup.de/" } target={ "_blank" } rel="noreferrer">
                                        www.dedalusgroup.de
                                    </a>
                                    <br/>
                                    Copyright (C) 2024
                                </p>
                            </div>
                            { selectedCountry?.companyInfo && <div className={ "custom-info" } dangerouslySetInnerHTML={ { __html: selectedCountry.companyInfo } }/> }
                        </div>
                    </div>
                    <div className={ "column-content" }>
                        <div className="software-identification">
                            <h2>{ resources.SoftwareIdentification }</h2>
                            <p className={ "pacsonweb-version-info" }>
                                DeepUnity PACSonWEB { !isMedicalDevice && "Core" } <b>V{ version }</b>
                            </p>
                            { selectedCountry?.productInfo && <div className={ "custom-info" } dangerouslySetInnerHTML={ { __html: selectedCountry.productInfo } }/> }
                            { isMedicalDevice && (
                                <div className={ "row-content" }>
                                    <img src="/POW4/img/icons/icnUDI.png" alt={ resources.SoftwareIdentification }/>
                                    <p>
                                        { softwareIdentificationNumber }
                                        { versionWithoutBuildNumber }
                                    </p>
                                </div>
                            ) }
                            <div className={ "row-content" }>
                                <img src="/POW4/img/icons/icnSoftwareIdentification.png" alt={ resources.SoftwareIdentification }/>
                                <p>
                                    { resources.Released } <b>{ productReleaseDate }</b>
                                </p>
                            </div>
                            { isMedicalDevice && (
                                <div className={ "row-content" }>
                                    <img src={ "/POW4/img/icons/icnCE2862.png" } alt={ "CE2862" }/>
                                    <img src={ "/POW4/img/icons/icnMD.png" } alt={ "MD" }/>
                                </div>
                            ) }
                            <ThirdPartySoftwareComponent/>
                        </div>
                        <div className="user-manuals">
                            <h2>{ resources.UserManuals }</h2>
                            <div className={ "row-content" }>
                                <img src="/POW4/img/icons/icnUserManuals.png" alt={ resources.UserManuals }/>
                                { selectedCountry?.documentationInfo &&
                                    <div className={ "custom-info" } dangerouslySetInnerHTML={ { __html: selectedCountry.documentationInfo } }/> }
                            </div>
                            <p
                                dangerouslySetInnerHTML={ {
                                    __html: sanitizeHtml(resources.ManualWarning.replace("{0}", "/POW3/Resource/LocalHelpFile").replace("{1}", "/POW3/Resource/GetManualPDF")),
                                } }
                            />
                            <p>
                                { resources.LastRevision }: <b>{ manualsRevisionDate }</b>
                            </p>
                        </div>
                    </div>
                </div>
                <div className={ "environment-machine-name" }>
                    ref: <span>{ serverCode }</span> { machineName && <span>({ machineName })</span> }
                </div>
            </div>
        );
    }
}
